import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { restoreCsrTheme } from '../../csr/csr-theme';

const API_URL = environment.apiHost;

interface UsageLogItem {
  id: number;
  extensionNumber: string;
  poolType: string;
  poolLabel: string;
  userId: number | null;
  userName: string | null;
  reservedAt: string | null;
  releasedAt: string | null;
  heldSeconds: number;
  callsCount: number;
  callDurationSeconds: number;
  utilizationPercent: number;
  messagesCount: number;
  conferenceCallsCount: number;
  longActive: boolean;
}

interface PoolType { code: string; label: string; }
interface MetricPoint { label: string; value: number; }
interface HourlyPoint { hour: string; calls: number; avgDurationSeconds: number; missed: number; abandoned: number; }
interface ActiveVsCallsPoint { hour: string; activeAgents: number; calls: number; }

interface UsageSummary {
  activeExtensions?: number;
  totalSessions: number;
  averageSessionSeconds: number;
  totalCalls: number;
  longestActiveSession?: UsageLogItem | null;
  longestSession: UsageLogItem | null;
}

interface CallLoad {
  hourly: HourlyPoint[];
  peakHour: { hour: string; calls: number };
  callsByExtension: MetricPoint[];
  callsByAgent: MetricPoint[];
  activeAgentsVsCalls: ActiveVsCallsPoint[];
  missedCalls: number;
  abandonedCalls: number;
}

@Component({
  selector: 'app-extension-logs',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './extension-logs.component.html',
  styleUrls: ['./extension-logs.component.scss'],
})
export class ExtensionLogsComponent implements OnInit {

  items: UsageLogItem[] = [];
  poolTypes: PoolType[] = [];
  loading = false;
  loadError = '';

  extensionFilter = '';
  poolFilter = '';
  userFilter = '';
  statusFilter = '';
  fromDate = '';
  toDate = '';

  page = 0;
  pageSize = 20;
  totalItems = 0;
  totalPages = 0;

  summary: UsageSummary = {
    activeExtensions: 0,
    totalSessions: 0,
    averageSessionSeconds: 0,
    totalCalls: 0,
    longestActiveSession: null,
    longestSession: null,
  };

  callLoad: CallLoad = {
    hourly: [],
    peakHour: { hour: '-', calls: 0 },
    callsByExtension: [],
    callsByAgent: [],
    activeAgentsVsCalls: [],
    missedCalls: 0,
    abandonedCalls: 0,
  };

  ngOnInit(): void {
    restoreCsrTheme();
    void this.loadPoolTypes();
    void this.load();
  }

  private authHeaders(): Record<string, string> {
    const token = localStorage.getItem('auth-token') ?? '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async loadPoolTypes(): Promise<void> {
    try {
      const res = await fetch(`${API_URL}/api/v1/telephony/extensions/pool-types`, {
        headers: this.authHeaders(),
      });
      const json = await res.json();
      this.poolTypes = Array.isArray(json?.data) ? json.data : [];
    } catch {
      this.poolTypes = [];
    }
  }

  async load(resetPage = false): Promise<void> {
    if (resetPage) this.page = 0;
    this.loading = true;
    this.loadError = '';
    const params = new URLSearchParams({
      page: String(this.page),
      size: String(this.pageSize),
    });
    if (this.extensionFilter.trim()) params.set('extension', this.extensionFilter.trim());
    if (this.poolFilter) params.set('pool', this.poolFilter);
    if (this.userFilter.trim()) params.set('user', this.userFilter.trim());
    if (this.statusFilter) params.set('status', this.statusFilter);
    if (this.fromDate) params.set('from', this.fromDate);
    if (this.toDate) params.set('to', this.toDate);

    try {
      const res = await fetch(`${API_URL}/api/v1/telephony/extensions/usage-logs?${params}`, {
        headers: this.authHeaders(),
      });
      const json = await res.json();
      if (!res.ok) {
        this.items = [];
        this.totalItems = 0;
        this.totalPages = 0;
        this.loadError = json?.message || json?.errors || `Failed to load usage logs (${res.status})`;
        return;
      }
      const data = json?.data ?? {};
      this.items = Array.isArray(data.items) ? data.items : [];
      this.totalItems = data.totalItems ?? 0;
      this.totalPages = data.totalPages ?? 0;
      this.page = data.page ?? 0;
      this.summary = data.summary ?? this.summary;
      this.callLoad = data.callLoad ?? this.callLoad;
    } catch (e: any) {
      this.loadError = e?.message ?? 'Failed to load';
    } finally {
      this.loading = false;
    }
  }

  clearFilters(): void {
    this.extensionFilter = '';
    this.poolFilter = '';
    this.userFilter = '';
    this.statusFilter = '';
    this.fromDate = '';
    this.toDate = '';
    void this.load(true);
  }

  goPage(p: number): void {
    if (p < 0 || p >= this.totalPages) return;
    this.page = p;
    void this.load();
  }

  formatDuration(seconds: number): string {
    if (!seconds) return '-';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h) return `${h}h ${m}m`;
    if (m) return `${m}m ${s}s`;
    return `${s}s`;
  }

  utilization(l: UsageLogItem): string {
    return `${Math.round(l.utilizationPercent || 0)}%`;
  }

  maxMetric(items: any[], key = 'value'): number {
    return Math.max(1, ...items.map((item) => Number(item?.[key]) || 0));
  }

  barWidth(value: number, max: number): string {
    return `${Math.max(4, Math.round((Number(value || 0) / Math.max(1, max)) * 100))}%`;
  }

  async exportCsv(): Promise<void> {
    const params = new URLSearchParams();
    if (this.extensionFilter.trim()) params.set('extension', this.extensionFilter.trim());
    if (this.poolFilter) params.set('pool', this.poolFilter);
    if (this.userFilter.trim()) params.set('user', this.userFilter.trim());
    if (this.statusFilter) params.set('status', this.statusFilter);
    if (this.fromDate) params.set('from', this.fromDate);
    if (this.toDate) params.set('to', this.toDate);
    this.loadError = '';
    try {
      const res = await fetch(`${API_URL}/api/v1/telephony/extensions/usage-logs/export?${params}`, {
        headers: this.authHeaders(),
      });
      if (!res.ok) {
        this.loadError = `CSV export failed (${res.status})`;
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'extension-usage-logs.csv';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      this.loadError = e?.message ?? 'CSV export failed';
    }
  }

  async forceRelease(l: UsageLogItem): Promise<void> {
    if (l.releasedAt) return;
    const ok = window.confirm(`Force release extension ${l.extensionNumber}?`);
    if (!ok) return;
    this.loading = true;
    this.loadError = '';
    try {
      const res = await fetch(`${API_URL}/api/v1/telephony/extensions/${encodeURIComponent(l.extensionNumber)}/force-release`, {
        method: 'POST',
        headers: this.authHeaders(),
      });
      const json = await res.json();
      if (!res.ok) {
        this.loadError = json?.message || `Force release failed (${res.status})`;
        return;
      }
      await this.load();
    } catch (e: any) {
      this.loadError = e?.message ?? 'Force release failed';
    } finally {
      this.loading = false;
    }
  }
}
